import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import { useAction } from "../../hooks/useAction";
import { useSession } from "../../session/SessionContext";
import { routeToHash } from "../../routes";
import { trpc } from "../../trpc";
import { Button } from "../shared/Button";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { useFormStyles } from "../shared/styles";
import { AuthLayout } from "./AuthLayout";

export const LoginPage = ({ expired }: { expired: boolean }) => {
  const form = useFormStyles();
  const { signedIn } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const login = useAction(
    useCallback(
      (input: { email: string; password: string }) =>
        trpc.auth.login.mutate(input),
      []
    )
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const user = await login.run({ email, password });
    if (user) {
      signedIn(user);
    }
  };

  return (
    <AuthLayout width={400}>
      {expired && !login.error && (
        <Notice kind="info">Your session ended. Please sign in again.</Notice>
      )}
      {login.error && <Notice kind="error">{login.error}</Notice>}
      <form onSubmit={(e) => void submit(e)} noValidate>
        <div className={form.stack}>
          <Field label="Email" htmlFor="login-email">
            <input
              id="login-email"
              type="email"
              className={form.control}
              autoComplete="username email"
              autoCapitalize="none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          <Field label="Password" htmlFor="login-password">
            <input
              id="login-password"
              type="password"
              className={form.control}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Field>
        </div>
        <div className={form.actions}>
          <Button type="submit" disabled={login.pending || !email || !password}>
            {login.pending ? "Signing in…" : "Sign in"}
          </Button>
          <a href={routeToHash({ page: "forgotPassword" })}>
            Forgot your password?
          </a>
        </div>
      </form>
      <p className={form.finePrint}>
        New here? Accounts are by invitation: ask the boat owner to invite your
        email address.
      </p>
    </AuthLayout>
  );
};
